/**
 * 플랫폼 감지 핵심 기능 - Tauri 기본 API 활용
 */

// Tauri 플러그인 동적 import (웹 환경에서도 동작하도록)
let tauriOs: any = null;

// Tauri OS 플러그인 초기화
export const initTauriOs = async () => {
  if (typeof window !== 'undefined' && window.__TAURI__) {
    try {
      // Tauri 환경에서만 플러그인 로드
      const module = await import('@tauri-apps/plugin-os');
      tauriOs = module;
      return true;
    } catch (e) {
      console.warn('[PlatformDetection] Tauri OS plugin not available:', e);
      return false;
    }
  }
  return false;
};

export type PlatformType = 
  | 'vscode'        // VSCode Extension WebView
  | 'jetbrains'     // JetBrains IDE Plugin
  | 'electron'      // Electron Desktop App
  | 'tauri-desktop' // Tauri Desktop App
  | 'tauri-mobile'  // Tauri Mobile App (iOS/Android)
  | 'web'           // Standard Web Browser
  | 'unknown';

export interface PlatformInfo {
  isTauri: boolean;
  isMobile: boolean;
  isDesktop: boolean;
  isWeb: boolean;
  isVSCode: boolean;
  isJetBrains: boolean;
  isElectron: boolean;
  platform: PlatformType;
  os?: string; // Tauri API에서 제공하는 실제 OS 이름
  arch?: string; // 아키텍처 정보
}

/**
 * 현재 플랫폼 정보를 반환 (Tauri 기본 API 활용)
 */
export const getPlatformInfo = async (): Promise<PlatformInfo> => {
  // VSCode WebView 감지
  if (typeof window !== 'undefined' && (window as any).acquireVsCodeApi !== 'undefined') {
    return {
      isTauri: false,
      isMobile: false,
      isDesktop: true,
      isWeb: false,
      isVSCode: true,
      isJetBrains: false,
      isElectron: false,
      platform: 'vscode'
    };
  }
  
  // JetBrains IDE 감지
  if (typeof window !== 'undefined' && (window as any).jetBrainsApi !== 'undefined') {
    return {
      isTauri: false,
      isMobile: false,
      isDesktop: true,
      isWeb: false,
      isVSCode: false,
      isJetBrains: true,
      isElectron: false,
      platform: 'jetbrains'
    };
  }
  
  // Electron 감지
  if (typeof window !== 'undefined' && (
    (window as any).electron !== 'undefined' || 
    (typeof process !== 'undefined' && process.versions && (process.versions as any).electron)
  )) {
    return {
      isTauri: false,
      isMobile: false,
      isDesktop: true,
      isWeb: false,
      isVSCode: false,
      isJetBrains: false,
      isElectron: true,
      platform: 'electron'
    };
  }
  
  // Tauri 환경 감지
  const isTauri = typeof window !== 'undefined' && window.__TAURI__;
  
  if (!isTauri) {
    // 웹 환경
    return {
      isTauri: false,
      isMobile: false,
      isDesktop: false,
      isWeb: true,
      isVSCode: false,
      isJetBrains: false,
      isElectron: false,
      platform: 'web'
    };
  }
  
  // Tauri 환경 - OS 플러그인 초기화
  const tauriAvailable = await initTauriOs();
  
  if (tauriAvailable && tauriOs) {
    try {
      // Tauri 공식 API로 플랫폼 정보 가져오기
      const platform = await tauriOs.platform();
      const arch = await tauriOs.arch();
      
      // 모바일 플랫폼 판단 (ios, android)
      const isMobile = platform === 'ios' || platform === 'android';
      const isDesktop = !isMobile;
      
      return {
        isTauri: true,
        isMobile,
        isDesktop,
        isWeb: false,
        isVSCode: false,
        isJetBrains: false,
        isElectron: false,
        platform: isMobile ? 'tauri-mobile' : 'tauri-desktop',
        os: platform,
        arch
      };
    } catch (e) {
      console.error('[PlatformDetection] Error getting Tauri platform info:', e);
    }
  }
  
  // Tauri API 사용 불가 시 폴백 (레거시 방식)
  console.warn('[PlatformDetection] Falling back to legacy detection');
  const isMobile = typeof window !== 'undefined' && (
    window.__TAURI_PLATFORM__ === 'android' || 
    window.__TAURI_PLATFORM__ === 'ios' ||
    (typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent))
  );
  
  return {
    isTauri: true,
    isMobile,
    isDesktop: !isMobile,
    isWeb: false,
    isVSCode: false,
    isJetBrains: false,
    isElectron: false,
    platform: isMobile ? 'tauri-mobile' : 'tauri-desktop'
  };
};

/**
 * 동기식 플랫폼 정보 반환 (캐시된 값 사용)
 * 초기 로드 시 getPlatformInfo()를 한 번 호출 후 이 함수 사용
 */
let cachedPlatformInfo: PlatformInfo | null = null;

export const getPlatformInfoSync = (): PlatformInfo => {
  if (cachedPlatformInfo) {
    return cachedPlatformInfo;
  }
  
  // VSCode WebView 감지
  if (typeof window !== 'undefined' && (window as any).acquireVsCodeApi !== 'undefined') {
    return {
      isTauri: false,
      isMobile: false,
      isDesktop: true,
      isWeb: false,
      isVSCode: true,
      isJetBrains: false,
      isElectron: false,
      platform: 'vscode'
    };
  }
  
  // JetBrains IDE 감지
  if (typeof window !== 'undefined' && (window as any).jetBrainsApi !== 'undefined') {
    return {
      isTauri: false,
      isMobile: false,
      isDesktop: true,
      isWeb: false,
      isVSCode: false,
      isJetBrains: true,
      isElectron: false,
      platform: 'jetbrains'
    };
  }
  
  // Electron 감지
  if (typeof window !== 'undefined' && (
    (window as any).electron !== 'undefined' || 
    (typeof process !== 'undefined' && process.versions && (process.versions as any).electron)
  )) {
    return {
      isTauri: false,
      isMobile: false,
      isDesktop: true,
      isWeb: false,
      isVSCode: false,
      isJetBrains: false,
      isElectron: true,
      platform: 'electron'
    };
  }
  
  // 캐시가 없으면 동기식 폴백 사용
  const isTauri = typeof window !== 'undefined' && window.__TAURI__;
  
  if (!isTauri) {
    return {
      isTauri: false,
      isMobile: false,
      isDesktop: false,
      isWeb: true,
      isVSCode: false,
      isJetBrains: false,
      isElectron: false,
      platform: 'web'
    };
  }
  
  // 레거시 방식으로 판단
  const isMobile = typeof window !== 'undefined' && (
    window.__TAURI_PLATFORM__ === 'android' || 
    window.__TAURI_PLATFORM__ === 'ios' ||
    (typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent))
  );
  
  return {
    isTauri: true,
    isMobile,
    isDesktop: !isMobile,
    isWeb: false,
    isVSCode: false,
    isJetBrains: false,
    isElectron: false,
    platform: isMobile ? 'tauri-mobile' : 'tauri-desktop'
  };
};

/**
 * 플랫폼 정보 캐시 초기화
 */
export const initPlatformCache = async () => {
  cachedPlatformInfo = await getPlatformInfo();
  return cachedPlatformInfo;
};

/**
 * 특정 플랫폼인지 확인하는 헬퍼 함수
 */
export const isPlatform = (platform: PlatformType): boolean => {
  const info = getPlatformInfoSync();
  return info.platform === platform;
};

/**
 * VSCode 환경인지 확인 (레거시 호환성)
 */
export const isVSCodeEnvironment = (): boolean => {
  const info = getPlatformInfoSync();
  return info.isVSCode;
};

/**
 * 플랫폼 감지 함수 (레거시 호환성)
 */
export const detectPlatform = (): PlatformType => {
  const info = getPlatformInfoSync();
  return info.platform;
};

// Global type declaration for Tauri
declare global {
  interface Window {
    __TAURI__?: any;
    __TAURI_PLATFORM__?: string;
  }
}

// 앱 시작 시 플랫폼 캐시 초기화 권장
// Note: 이 초기화는 빌드 시 실행되지 않도록 런타임에서만 실행
if (typeof globalThis !== 'undefined' && typeof window !== 'undefined' && globalThis === window) {
  // 브라우저 환경에서만 초기화
  initPlatformCache().catch(e => {
    console.warn('[PlatformDetection] Failed to initialize platform cache:', e);
  });
}